import { FC, PropsWithChildren, ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";
import { compareFractionalOrder } from "@/lib/compare-fractional-order";

import { useGetPriorityListForWorkspace } from "../hooks/api/use-get-priority-list-for-workspace";
import { Priority } from "../models/priority-model";

import { PrioritySelectDropdownItem } from "./priority-select-dropdown-item";

export interface PrioritySelectDropdownProps extends PropsWithChildren {
  workspaceId: string;
  asChild?: boolean;
  selected?: {
    priorityId: string;
  } | null;
  onSelect?: (priority: { id: string } | null) => void;
  side?: "top" | "right" | "bottom" | "left";
  isReadOnly?: boolean;
}

export const PrioritySelectDropdown: FC<PrioritySelectDropdownProps> = ({
  workspaceId,
  children,
  side,
  asChild,
  onSelect,
  isReadOnly,
  ...props
}) => {
  const { t } = useTranslation("common");
  const [selectedPriority, setSelectedPriority] = useState(props.selected ? props.selected : null);
  const { priorityList } = useGetPriorityListForWorkspace({ workspaceId: workspaceId });

  const comboboxOptions = useMemo(() => {
    const priorityOptions = priorityList
      .slice()
      .sort((a, b) => compareFractionalOrder(a.order, b.order))
      .flatMap((u) => {
        if (!u) {
          return [];
        }
        return {
          id: u.id,
          value: u.name,
          original: u,
        };
      });
    return [{ id: "-1", value: t("notAssigned"), original: null }, ...priorityOptions];
  }, [priorityList, t]);

  const renderItem = (option: ComboboxOption<Priority>): ReactElement => {
    const priority = option.original;

    const selected = (option.id === "-1" && selectedPriority === null) || selectedPriority?.priorityId === option.id;
    return <PrioritySelectDropdownItem priority={priority} selected={selected} />;
  };

  const handleUserSelected = (selectedOption: ComboboxOption<Priority> | null) => {
    if (!selectedOption) {
      return;
    }

    if (selectedOption.id === "-1") {
      setSelectedPriority(null);
      if (onSelect) {
        onSelect(null);
      }
      return;
    }

    setSelectedPriority({ priorityId: selectedOption.id });
    if (onSelect) {
      onSelect(selectedOption);
    }
  };

  useEffect(() => {
    if (props.selected) {
      setSelectedPriority(props.selected);
    }
  }, [props.selected]);

  if (!comboboxOptions) {
    return <></>;
  }

  return (
    <Combobox
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleUserSelected}
      side={side}
      isReadOnly={isReadOnly}
    >
      {children}
    </Combobox>
  );
};
